import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import {
  AppBar,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import barcodeScanner from "components/barcodeScan/barcodeScanner";
import { MachineAutocomplete } from "components/machine/MachineAutocomplete";
import PrimaryButton from "components/PrimaryButton";
import { PromptDialog } from "components/PromptDialog";
import { isAbortError } from "helpers";
import { MachineType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  changeJobMachine,
  selectIsJobInProgress,
  selectSelectedJob,
  setChangeJobMachineOpen,
} from "store/slices/partner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

interface ChangeJobMachineDialogProps {
  handleFinished: () => void;
}

export const ChangeJobMachineDialog: FC<ChangeJobMachineDialogProps> = (props) => {
  const { handleFinished } = props;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { loading, open } = useAppSelector((state) => state.partner.changeJobMachine);
  const { machinesLoading } = useAppSelector((state) => state.machineAutocomplete);

  const [machineLoading, setMachineLoading] = useState(false);
  const [openVisitInProgressDialog, setOpenVisitInProgressDialog] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<MachineType | null>(null);
  const currentMachine = job?.machine;
  const machineInstallDate = currentMachine?.installDate;
  const jobInProgress = useAppSelector(selectIsJobInProgress);

  const close = useCallback(() => {
    setSelectedMachine(null);
    dispatch(setChangeJobMachineOpen({ open: false }));
  }, [dispatch]);

  const changeJobMachineCb = useCallback(
    (machineId: string) => {
      dispatch(changeJobMachine({ jobId: job.id, machineId }))
        .then(unwrapResult)
        .then(() => {
          setMachineLoading(false);
          handleFinished();
          close();
          dispatch(addSnackbarMessage({ key: "ChangeJobMachine-success" }));
        })
        .catch((error: any) => {
          if (isAbortError(error)) return;
          if (Array.isArray(error) && error.some((e) => e.message.includes("403 Forbidden"))) {
            dispatch(addSnackbarMessage({ key: "ChangeJobMachine-forbidden" }));
          } else {
            dispatch(addSnackbarMessage({ key: "ChangeJobMachine-fail" }));
          }
          setMachineLoading(false);
        });
    },
    [close, dispatch, handleFinished, job.id],
  );

  const onSave = useCallback(() => {
    setMachineLoading(true);
    changeJobMachineCb(selectedMachine?.id || "");
  }, [changeJobMachineCb, selectedMachine?.id]);

  const checkVisitInProgress = useCallback(() => {
    if (jobInProgress) {
      setOpenVisitInProgressDialog(true);
    } else {
      onSave();
    }
  }, [jobInProgress, onSave]);

  useEffect(() => {
    barcodeScanner.initialize();
    return () => {
      barcodeScanner.turnOff();
    };
  }, []);

  return (
    <Dialog fullScreen open={open} onClose={close} data-testid="ChangeJobMachine-Dialog">
      <BackdropPrimaryMain open={machineLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="ChangeJobMachineDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            {`${job?.externalId} `} / <FormattedMessage id="visit.changeMachine" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        {currentMachine && (
          <CardHeader
            data-testid="ChangeJobMachine-currentMachine"
            sx={{
              fontSize: "1.2rem",
            }}
            subheader={
              <>
                <Typography fontWeight={700}>{currentMachine.name}</Typography>
                <Typography>{currentMachine.machineNo}</Typography>
                {currentMachine.idNo && <Typography>{currentMachine.idNo}</Typography>}
                {currentMachine.location && <Typography>{currentMachine.location}</Typography>}
                {machineInstallDate && (
                  <Typography>
                    {`${intl.formatMessage({ id: "general.installed" })} `}
                    <FormattedDate
                      value={machineInstallDate}
                      day="numeric"
                      month="long"
                      year="numeric"
                    />
                  </Typography>
                )}
              </>
            }
          />
        )}
        {!currentMachine && (
          <CardHeader
            data-testid="ChangeJobMachine-currentMachine"
            sx={{
              fontSize: "1.2rem",
            }}
            subheader={
              <>
                <Typography fontWeight={700}>
                  <FormattedMessage id="changeJobEquipment.noMachine" />
                </Typography>
                <Typography></Typography>
              </>
            }
          />
        )}
        <CardContent sx={{ paddingTop: 0 }}>
          {!machineLoading && (
            <>
              <Grid container direction="column" spacing={2}>
                <Grid
                  item
                  container
                  alignItems="start"
                  justifyContent="space-between"
                  wrap="nowrap"
                >
                  <MachineAutocomplete
                    customerId={job.customer?.id}
                    onChange={setSelectedMachine}
                  />
                </Grid>

                <Grid item>
                  <PrimaryButton
                    key={`change-machine-submit-button`}
                    fullWidth
                    variant="contained"
                    disabled={!selectedMachine || loading || machinesLoading}
                    onClick={checkVisitInProgress}
                    data-testid="ChangeJobMachine-SaveButton"
                  >
                    <FormattedMessage id="general.save" />
                  </PrimaryButton>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
      <PromptDialog
        open={openVisitInProgressDialog}
        setOpen={setOpenVisitInProgressDialog}
        data-testid="ChangeJobMachine-VisitInProgressPrompt"
        onOk={() => {
          onSave();
        }}
        promptContent={<FormattedMessage id="changeJobEquipment.inProgressVisitReset" />}
      />
    </Dialog>
  );
};
